type PeriodType = "7_days" | "30_days";

export type Locale = "es" | "en";

function messageCounts(channels: Record<string, number> | undefined) {
  const wapp = (channels?.wapp || 0) + (channels?.fake || 0);
  const mail = channels?.mail || 0;
  const panel = channels?.panel || 0;
  return { wapp, mail, panel, total: wapp + mail + panel };
}

function getPeriodLabel(
  periodIndex: number,
  period: PeriodType,
  locale: Locale
): string {
  if (periodIndex === 2) {
    return locale === "es"
      ? period === "7_days"
        ? "Esta semana"
        : "Este mes"
      : period === "7_days"
        ? "This week"
        : "This month";
  }

  if (locale === "es") {
    return period === "7_days" ? `Semana ${periodIndex + 1}` : `Mes ${periodIndex + 1}`;
  }
  return period === "7_days" ? `Week ${periodIndex + 1}` : `Month ${periodIndex + 1}`;
}

interface ReportData {
  condoId: number | null;
  condoName: string;
  channels: Record<string, number>;
  periodIndex?: number; // 0, 1, 2 for Semana 1, Semana 2, Esta semana
}

interface CasesReportData {
  condoId: number;
  condoName: string;
  types: Record<string, number>;
  categories: Record<string, number>;
  periodIndex?: number; // 0, 1, 2 for Semana 1, Semana 2, Esta semana
}

interface ChartDataPoint {
  name: string;
  [key: string]: string | number;
}

export function transformMessagesData(
  data: ReportData[],
  selectedCondoIds: number[],
  period: PeriodType,
  showPeriods: boolean,
  locale: Locale
): ChartDataPoint[] {
  if (showPeriods) {
    // Single condo: show periods
    // Backend returns data with periodIndex, so we group by periodIndex
    const dataByPeriod: Record<number, ReportData> = {};
    data.forEach((item) => {
      if (item.periodIndex !== undefined) {
        dataByPeriod[item.periodIndex] = item;
      }
    });

    // Generate 3 periods (0, 1, 2) and map to chart data
    return [0, 1, 2].map((index) => {
      const periodData = dataByPeriod[index];
      const counts = messageCounts(periodData?.channels);
      return {
        name: getPeriodLabel(index, period, locale),
        ...counts,
      };
    });
  }

  // Multiple condos or none: show buildings
  return data.map((item) => {
    const counts = messageCounts(item.channels);
    return {
      name: item.condoName,
      ...counts,
    };
  });
}

type CaseTypeKey = "issue_high" | "issue_medium" | "issue_low" | "non_issue" | "unknown";

const CASE_TYPE_KEYS: CaseTypeKey[] = [
  "issue_high",
  "issue_medium",
  "issue_low",
  "non_issue",
  "unknown",
];

function normalizeTypeKey(raw: string): CaseTypeKey | null {
  switch (raw) {
    case "issue_high":
    case "issueHigh":
      return "issue_high";
    case "issue_medium":
    case "issueMedium":
      return "issue_medium";
    case "issue_low":
    case "issueLow":
      return "issue_low";
    case "non_issue":
    case "nonIssue":
      return "non_issue";
    case "unknown":
      return "unknown";
    default:
      return null;
  }
}

function normalizeCategoryKey(raw: string): string {
  // Normalize both snake_case and camelCase into a stable camelCase key.
  const map: Record<string, string> = {
    infrastructureMaintenance: "infrastructureMaintenance",
    infrastructure_maintenance: "infrastructureMaintenance",
    sanitaryIssues: "sanitaryIssues",
    sanitary_issues: "sanitaryIssues",
    neighborRelations: "neighborRelations",
    neighbor_relations: "neighborRelations",
    commonExpensesAdmin: "commonExpensesAdmin",
    common_expenses_admin: "commonExpensesAdmin",
    movesAndWorks: "movesAndWorks",
    moves_and_works: "movesAndWorks",
    securityAccess: "securityAccess",
    security_access: "securityAccess",
    emergencies: "emergencies",
    pets: "pets",
    amenities: "amenities",
    generalInformation: "generalInformation",
    general_information: "generalInformation",
  };

  return map[raw] || raw;
}

export function transformCasesByTypeData(
  casesData: CasesReportData[],
  period: PeriodType,
  showPeriods: boolean,
  locale: Locale
): ChartDataPoint[] {
  if (showPeriods) {
    // Single condo: show periods
    // Backend returns data with periodIndex, so we group by periodIndex
    const dataByPeriod: Record<number, CasesReportData> = {};
    casesData.forEach((item) => {
      if (item.periodIndex !== undefined) {
        dataByPeriod[item.periodIndex] = item;
      }
    });

    // Generate 3 periods (0, 1, 2) and map to chart data
    return [0, 1, 2].map((index) => {
      const periodData = dataByPeriod[index];
      const dataPoint: ChartDataPoint = {
        name: getPeriodLabel(index, period, locale),
      };

      CASE_TYPE_KEYS.forEach((key) => {
        dataPoint[key] = 0;
      });

      if (periodData && periodData.types) {
        Object.entries(periodData.types).forEach(([type, count]) => {
          const normalizedType = normalizeTypeKey(type);
          if (normalizedType) {
            dataPoint[normalizedType] = count;
          }
        });
      }

      return dataPoint;
    });
  }

  // Multiple condos or none: show buildings
  return casesData.map((item) => {
    const dataPoint: ChartDataPoint = {
      name: item.condoName,
    };

    CASE_TYPE_KEYS.forEach((key) => {
      dataPoint[key] = 0;
    });

    if (item.types) {
      Object.entries(item.types).forEach(([type, count]) => {
        const normalizedType = normalizeTypeKey(type);
        if (normalizedType) {
          dataPoint[normalizedType] = count;
        }
      });
    }

    return dataPoint;
  });
}

export function transformCasesByCategoryData(
  casesData: CasesReportData[],
  period: PeriodType,
  showPeriods: boolean,
  locale: Locale
): ChartDataPoint[] {
  // Collect all category keys from all data
  const allCategoryKeys = new Set<string>();
  casesData.forEach((item) => {
    if (item.categories) {
      Object.keys(item.categories).forEach((category) => {
        allCategoryKeys.add(normalizeCategoryKey(category));
      });
    }
  });

  if (showPeriods) {
    // Single condo: show periods
    // Backend returns data with periodIndex, so we group by periodIndex
    const dataByPeriod: Record<number, CasesReportData> = {};
    casesData.forEach((item) => {
      if (item.periodIndex !== undefined) {
        dataByPeriod[item.periodIndex] = item;
      }
    });

    // Generate 3 periods (0, 1, 2) and map to chart data
    return [0, 1, 2].map((index) => {
      const periodData = dataByPeriod[index];
      const dataPoint: ChartDataPoint = {
        name: getPeriodLabel(index, period, locale),
      };

      Array.from(allCategoryKeys).forEach((key) => {
        dataPoint[key] = 0;
      });

      if (periodData && periodData.categories) {
        Object.entries(periodData.categories).forEach(([category, count]) => {
          const normalizedKey = normalizeCategoryKey(category);
          dataPoint[normalizedKey] = count;
        });
      }

      return dataPoint;
    });
  }

  // Multiple condos or none: show buildings
  return casesData.map((item) => {
    const dataPoint: ChartDataPoint = {
      name: item.condoName,
    };

    Array.from(allCategoryKeys).forEach((key) => {
      dataPoint[key] = 0;
    });

    if (item.categories) {
      Object.entries(item.categories).forEach(([category, count]) => {
        const normalizedKey = normalizeCategoryKey(category);
        dataPoint[normalizedKey] = count;
      });
    }

    return dataPoint;
  });
}
